import { DateFormat, FormField, QuestionnaireDescription, useError } from "common";
import dayjs from "dayjs";
import { useField } from "formik";
import React, { useMemo } from "react";
import { FormatInput } from "../Input/FormatInput";
import { FieldProps } from "./TextField";

// Computed once — date format placeholder never changes at runtime
const datePlaceholder = (() => {
    let placeholder = dayjs().endOf("year").format(DateFormat.DATE_SHORT);
    placeholder = placeholder.replace("12", "MM");
    placeholder = placeholder.replace("31", "DD");
    placeholder = placeholder.replace(dayjs().year().toString(), "YYYY");
    return placeholder;
})();

const isoPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const toDisplayValue = (value: string): string => {
    if (!value) {
        return "";
    }

    const mmddyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (mmddyyyyPattern.test(value)) {
        return value;
    }

    const match = isoPattern.exec(value);
    if (match) {
        return `${match[2]}/${match[3]}/${match[1]}`;
    }

    return value;
};

export const DateOfBirthField = ({
    name,
    field,
    isRequired,
    classNames,
    showDescription = true,
    onChange,
}: FieldProps) => {
    const [{ value, ...inputProps }, , helpers] = useField(name);
    const error = useError(name);

    const displayValue = useMemo(() => toDisplayValue(value), [value]);

    const handleValueChange = (values: { formattedValue?: string; value?: string }) => {
        const stringValue = values.formattedValue ?? values.value ?? "";
        helpers.setValue(stringValue);
        onChange?.(stringValue);
    };

    return (
        <FormField label={field.label} name={name} classNames={{ label: classNames?.label }}>
            {showDescription && <QuestionnaireDescription field={field} />}
            <FormatInput
                {...inputProps}
                value={displayValue}
                isError={!!error}
                allowEmptyFormatting={false}
                format="##/##/####" // Hans off! Do not change format, otherwise you'll have to read the all this ticket related stuff https://xola01.atlassian.net/browse/X2-5120
                placeholder={datePlaceholder}
                isRequired={isRequired}
                onValueChange={handleValueChange}
            />
        </FormField>
    );
};

export const getISOFormattedDateString = (value: string, ensureDayAtEnd?: boolean) => {
    const pattern = /(\d{2})\D(\d{2})\D(\d{4})/gm;
    let groups = pattern.exec(value);
    if (groups && groups.length >= 4) {
        let formattedDate = `${groups[3]}-${groups[1]}-${groups[2]}`;
        if (ensureDayAtEnd && Number(groups[1]) > 12 && Number(groups[1]) <= 31 && Number(groups[2]) <= 12) {
            formattedDate = `${groups[3]}-${groups[2]}-${groups[1]}`;
        }

        return formattedDate;
    }

    const unexpectedPattern = /(\d{4})\D(\d{2})\D(\d{2})/gm;
    groups = unexpectedPattern.exec(value);
    if (groups && groups.length >= 4) {
        let formattedDate = `${groups[1]}-${groups[2]}-${groups[3]}`;
        if (Number(groups[2]) > 12 && Number(groups[2]) <= 31 && Number(groups[3]) <= 12) {
            formattedDate = `${groups[1]}-${groups[3]}-${groups[2]}`;
        }

        return formattedDate;
    }

    return value;
};
